/**
 * MC Block & Death Heatmap -- the `/mc/heatmap` page module (HEAT-01/02, 233-UI-SPEC.md).
 *
 * Initializes on `/mc/heatmap` only (guards on `#mc-heatmap-canvas` presence, mirroring every
 * other `/mc/*` module's own DOM-marker-guard idiom -- initAnalytics()/initLeaderboard()/
 * initFeed()). Renders a sparse canvas-2D block-density map -- canvas pixel dimensions derive
 * from the query's own bounding box (CELL_PX=10, PADDING_BUCKETS=4), NEVER a dense grid filled
 * from the raw coordinate magnitude (the eo-map.ts dense-loop anti-pattern this module
 * deliberately does not copy, 233-RESEARCH.md Pattern 1/Pitfall 4) -- with a toggleable
 * death-marker overlay sharing the exact same bucket grid, fetched via `?do=getDensityMap`/
 * `?do=getDeathMarkers` (UI-SPEC §7.10 locked signal names). CR-01 fix (233-REVIEW.md): the two
 * fetches are SEQUENCED, not concurrent -- `loadHeatmapData()` awaits the density response and
 * threads its (possibly T-233-03-coarsened) `effectiveBucketSize` into the deaths request, since
 * `getDeathMarkers()` has no other way to learn whether the sibling density call's DoS ceiling
 * coarsened the grid it must share. `fetchDensity()` never throws (internal try/catch, always
 * resolves with a usable bucket size), so this sequencing does not reintroduce a "one signal
 * failing takes the other down" regression (§7.7) -- a density fetch failure still lets the
 * death fetch proceed (on the BASE_BUCKET_SIZE fallback), and a death fetch failure still leaves
 * the density map fully usable (each fetch's own try/catch remains independent).
 *
 * Wired into `mc.ts`'s `static init()`, NOT `src/index.ts` (Ph232 Pitfall 7 -- the documented
 * wrong wiring point for a per-page MC module; `src/index.ts` only ever imports `./js/eo-map`'s
 * default export for the unrelated EO section, never any `./js/mc/*` submodule).
 *
 * ⚠ Every payload-derived string (death narrative, dimension label) is written via `textContent`
 * -- NEVER `innerHTML` (T-233-07). The `#mc-heatmap-bluemap-link` anchor's `href`/`textContent`
 * are built from DOM properties, never innerHTML, and only from the selected `<option>`'s
 * already-https-filtered `dataset.bluemapUrl` (T-233-08 -- 233-03's model guarantees any
 * non-empty value is `https://`; this module does no scheme re-check of its own).
 *
 * Known contract gap (documented, not a client bug): UI-SPEC §7.5 asks for a relative-time
 * suffix on the single-death tooltip, but `McHeatmapModel::getDeathMarkers()`'s locked JSON
 * contract (tools/probe-233.php's own authoritative docblock, unchanged by 233-04) exposes only
 * `narratives: string[]` per cluster -- no `occurred_at`/timestamp field reaches the client at
 * all. A relative-time suffix cannot be computed from data that was never sent; adding one would
 * require changing 233-03/04's already-shipped signal contract, which is out of this
 * frontend-only plan's scope (a Rule 4 architectural change, not a Rule 1-3 auto-fix). The
 * narrative string is rendered verbatim, without a suffix.
 */

import Panzoom from '@panzoom/panzoom';
import { dimensionPillClass, resolveMcLabel } from '../utils/mcKeys';
import { appendNarrativeSegments, buildCoordPill, type NarrativeSegment } from './coordPill';

// ── Constants (locked, UI-SPEC §7.3 -- must match tools/probe-233-heatmap-render.mjs's own
//    cross-check constants) ─────────────────────────────────────────────────────────────────
const CELL_PX = 10;
const PADDING_BUCKETS = 4;
const TOOLTIP_OFFSET_X = 12;
const TOOLTIP_OFFSET_Y = 12;
const DEATH_GLYPH_FONT_PX = 16; // UI-SPEC §7.5 -- roughly 1.5x CELL_PX
const DEATH_LABEL_FONT_PX = 13; // UI-SPEC §7.5 -- Data/mono role
// CR-01 fix (233-REVIEW.md) -- mirrors McHeatmapModel::BASE_BUCKET_SIZE exactly. Used ONLY as
// the death fetch's fallback bucket size when the paired density fetch itself failed (see
// fetchDensity()'s catch branch) -- the steady-state value always comes from the density
// payload's OWN effectiveBucketSize (see loadHeatmapData()).
const BASE_BUCKET_SIZE = 16;

// ── Payload contracts (McPresenter::handleGetDensityMap()/handleGetDeathMarkers(),
//    tools/probe-233.php's own authoritative docblock -- shapes confirmed byte-for-byte against
//    McHeatmapModel.php by 233-03/04-SUMMARY.md) ───────────────────────────────────────────────
interface DensityBucket {
    bucketX: number;
    bucketZ: number;
    count: number;
}

interface DensityBoundingBox {
    minX: number;
    maxX: number;
    minZ: number;
    maxZ: number;
}

interface DensityPayload {
    dimensions: string[];
    dimensionLabels?: string[];
    activeDimension: string;
    buckets: DensityBucket[];
    boundingBox: DensityBoundingBox;
    maxCount: number;
    coarsened: boolean;
    effectiveBucketSize: number;
    empty: boolean;
    emptyReason: string | null;
}

interface DeathCluster {
    bucketX: number;
    bucketZ: number;
    count: number;
    // Phase 244 (D-01/D-02/D-04) -- the most-recent death's raw x/y/z, carried by
    // HeatmapBucketing::clusterDeaths() (244-01). Always the same death as narratives[0], the
    // server's own most-recent-first sort. This is the click-resolution target for a cluster
    // tooltip -- UI-SPEC §4.5 deliberately narrows a cluster's clickable coordinate to this one
    // death, never the other up-to-four narrative lines.
    pos: [number, number, number];
    narratives: string[];
    // Phase 254 (D-08) -- one entry per rendered tooltip line, parallel-indexed to `narratives`,
    // each entry itself an ordered segment array (254-UI-SPEC.md §9.7). Optional: a heatmap page
    // loaded before this deploy re-fetches `?do=getDeathMarkers` on every server or dimension tab
    // change, so a cluster can arrive from either side of the deploy -- every consumer below
    // degrades per-line to the retained `narratives` strings when this is absent or short.
    narrative_segments?: NarrativeSegment[][];
}

interface DeathPayload {
    clusters: DeathCluster[];
    empty: boolean;
}

// ── State ───────────────────────────────────────────────────────────────────────────────────
let currentServer = '';
let currentDimension = '';
// Phase 244 (D-01/D-02/D-04/T-244-21) -- the BlueMap href resolved by the LAST pointermove hit
// test, cached so the canvas click listener never has to recompute the hit test (it is
// guaranteed fresh -- pointermove always fires before a click on the same target). Assigned on
// every pointermove branch (cluster / bucket / no-hit) and cleared on mouseleave -- no path
// leaves it stale.
let hoveredHref: string | null = null;
let densityPayload: DensityPayload | null = null;
let deathClusters: DeathCluster[] = [];
let deathsEmpty = true;
let showDeaths = true;
let pz: ReturnType<typeof Panzoom> | null = null;
// WR-01 fix (233-REVIEW.md) -- monotonically increasing id, bumped once per loadHeatmapData()
// call and shared by BOTH the density and deaths fetch it triggers. Rapid server/dimension
// switching can let an EARLIER request's response resolve AFTER a newer one was already issued
// (network reordering) -- every fetch below re-checks this id against the value it captured
// before awaiting, and discards its response (never overwrites densityPayload/deathClusters or
// redraws) the moment it no longer matches the current load.
let heatmapRequestId = 0;

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

export function initHeatmap(): void {
    // DOM-marker guard: no-op on every page that does NOT have the heatmap canvas.
    const canvas = document.getElementById('mc-heatmap-canvas') as HTMLCanvasElement | null;
    if (!canvas) return;

    const serverSelect = document.getElementById('mc-heatmap-server-select') as HTMLSelectElement | null;
    const tooltip = document.getElementById('mc-heatmap-tooltip');
    const dimensionTabs = document.getElementById('mc-heatmap-dimension-tabs');
    if (!serverSelect || !tooltip || !dimensionTabs) return;

    // D-06: pre-select a server named by ?server=<key> if that option actually exists on
    // THIS page's picker. Heatmap has no "all servers" fallback (unlike Feed) — an absent
    // match must silently no-op and keep the server-rendered default (Pitfall 1).
    const deepLinkServer = new URLSearchParams(window.location.search).get('server');
    if (deepLinkServer && serverSelect.querySelector(`option[value="${CSS.escape(deepLinkServer)}"]`)) {
        serverSelect.value = deepLinkServer;
    }

    currentServer = serverSelect.value;

    wireTooltip(canvas, tooltip, serverSelect);

    serverSelect.addEventListener('change', () => {
        currentServer = serverSelect.value;
        currentDimension = ''; // server re-resolves its own default dimension (§7.6)
        updateBluemapLink(serverSelect);
        void loadHeatmapData(canvas, dimensionTabs, serverSelect);
    });

    dimensionTabs.addEventListener('click', (e) => {
        const target = (e.target as HTMLElement).closest<HTMLElement>('[data-dimension]');
        if (!target) return;
        e.preventDefault();
        const dim = target.dataset.dimension ?? '';
        if (dim === currentDimension) return;
        currentDimension = dim;
        markActiveTab(dimensionTabs, dim);
        // Refresh the whole-map button synchronously, mirroring the server-select handler above --
        // currentDimension is already set, so this reflects the newly-selected tab immediately
        // rather than waiting for fetchDensity()'s async re-run (254-REVIEW.md WR-02 fix: without
        // this call, the button briefly shows the PREVIOUS dimension's colour/href until that
        // fetch resolves).
        updateBluemapLink(serverSelect);
        void loadHeatmapData(canvas, dimensionTabs, serverSelect);
    });

    const deathToggle = document.getElementById('mc-heatmap-death-toggle') as HTMLInputElement | null;
    if (deathToggle) {
        showDeaths = deathToggle.checked;
        deathToggle.addEventListener('change', () => {
            showDeaths = deathToggle.checked;
            redraw(canvas);
            updateDeathEmptyHelper();
        });
    }

    // Update the BlueMap deep-link slot on initial load, on every server switch, and (as of
    // Phase 254 D-12) synchronously on every dimension-tab click too -- see that click handler
    // above. The map is per-server, but which MAP ID resolves is per-dimension (D-12).
    updateBluemapLink(serverSelect);

    // Initial fetch: dimension='' so the model resolves the overworld-or-first default (§7.6).
    void loadHeatmapData(canvas, dimensionTabs, serverSelect);
}

// ---------------------------------------------------------------------------
// Density fetch + render
// ---------------------------------------------------------------------------

/** Orchestrates the two per-load fetches (CR-01 fix, 233-REVIEW.md). Awaits fetchDensity()'s
 *  resolved bucket size (it never throws -- see its own catch branch -- so this never blocks the
 *  death fetch on a density failure) and threads it into fetchDeaths() so death clustering lands
 *  on the SAME grid the density layer resolved (including when T-233-03's ceiling coarsened it).
 *  fetchDeaths() itself is still fire-and-forget with its own independent try/catch (§7.7 -- a
 *  death-fetch failure never touches the density map).
 *
 *  WR-01 fix (233-REVIEW.md): bumps `heatmapRequestId` once per call and threads that id through
 *  both fetches, then re-checks it before firing the deaths request -- if a newer load has
 *  already started (rapid server/dimension switching) by the time the density fetch resolves,
 *  this load is abandoned entirely rather than firing a now-stale deaths request.
 *
 *  `select` (Phase 254 D-10/D-11/D-12) is threaded through to fetchDensity() so it can re-run
 *  updateBluemapLink() once the density fetch actually resolves the server's live map ids --
 *  needed for the initial-load and server-switch paths, where currentDimension isn't confirmed
 *  until this resolves. The dimension-tab click handler above ALSO calls updateBluemapLink()
 *  synchronously before this function ever runs (254-REVIEW.md WR-02 fix), so a tab switch shows
 *  the correct colour/href immediately; this async re-run just re-confirms it once fresh data
 *  lands, rather than being the only place it happens. */
async function loadHeatmapData(canvas: HTMLCanvasElement, dimensionTabs: HTMLElement, select: HTMLSelectElement): Promise<void> {
    const myId = ++heatmapRequestId;
    const bucketSize = await fetchDensity(canvas, dimensionTabs, myId, select);
    if (myId !== heatmapRequestId) return; // a newer load has since started -- abandon this one
    void fetchDeaths(canvas, bucketSize, myId);
}

async function fetchDensity(canvas: HTMLCanvasElement, dimensionTabs: HTMLElement, requestId: number, select: HTMLSelectElement): Promise<number> {
    const canvasWrap = canvas.closest<HTMLElement>('.mc-heatmap-canvas-wrap');
    const url = `?do=getDensityMap&server=${encodeURIComponent(currentServer)}`
        + `&dimension=${encodeURIComponent(currentDimension)}`;

    try {
        const resp = await fetch(url, { headers: { 'X-Requested-With': 'XMLHttpRequest' } });
        if (!resp.ok) throw new Error(String(resp.status));

        const payload: DensityPayload = await resp.json();
        // WR-01 -- a newer load has since been issued; this response is stale (network
        // reordering) -- discard it before it can overwrite state the newer request already
        // owns. The returned bucket size is never consumed by a stale caller (loadHeatmapData()
        // re-checks the same id immediately after awaiting this call).
        if (requestId !== heatmapRequestId) return payload.effectiveBucketSize;

        densityPayload = payload;
        currentDimension = payload.activeDimension;
        renderDimensionTabs(dimensionTabs, payload.dimensions, payload.dimensionLabels, payload.activeDimension);
        // Phase 254 D-10/D-11/D-12 -- currentDimension just became known/confirmed. Every caller
        // of loadHeatmapData() (initial load, a server switch, a dimension-tab switch) funnels
        // through this one resolution point, so re-running the whole-map button here -- rather
        // than only inside the dimension-tab click handler -- is what makes its live tint and
        // destination track the NOW-current dimension instead of the pre-fetch placeholder any
        // earlier synchronous updateBluemapLink() call used (see that function's own docblock).
        updateBluemapLink(select);

        if (payload.empty) {
            if (canvasWrap) canvasWrap.hidden = true;
            showEmptyState(payload.emptyReason);
            return payload.effectiveBucketSize;
        }

        hideEmptyState();
        if (canvasWrap) canvasWrap.hidden = false;
        resizeCanvas(canvas, payload.boundingBox);
        redraw(canvas);
        return payload.effectiveBucketSize;
    } catch (err) {
        if (requestId !== heatmapRequestId) return BASE_BUCKET_SIZE; // WR-01 -- stale failure, discard silently
        console.error('[mc-heatmap] Density fetch error:', err);
        densityPayload = null;
        if (canvasWrap) canvasWrap.hidden = true;
        renderDensityError();
        return BASE_BUCKET_SIZE; // CR-01 -- honest fallback; no density payload to thread a grid from.
    }
}

/** Sizes the canvas backbuffer from the query's OWN bounding box (never the raw coordinate
 *  magnitude -- 233-CONTEXT's "unbounded world extent" trap) and resets panzoom, since a
 *  server/dimension switch changes the backing bounding box (Pitfall 8). */
function resizeCanvas(canvas: HTMLCanvasElement, box: DensityBoundingBox): void {
    // Assigning canvas.width/height RESETS context state per the HTML Living Standard -- size
    // first, then any context defaults (eo-map.ts Pitfall 2 precedent). Locked formula (§7.3):
    // canvas dimensions derive from the query's OWN bounding box, never the raw coordinate
    // magnitude (a bucket at x=-311 must not imply a 311-bucket-wide canvas).
    canvas.width = (box.maxX - box.minX + 1 + 2 * PADDING_BUCKETS) * CELL_PX;
    canvas.height = (box.maxZ - box.minZ + 1 + 2 * PADDING_BUCKETS) * CELL_PX;

    wirePanzoom(canvas); // init-once (internally guarded); safe to call on every resize
    resetPanzoomTransform(canvas);
}

/** Base fill + sparse bucket draw. Re-resolves theme colors at draw time (not cached at module
 *  load) so a light/dark toggle repaints correctly. */
function redraw(canvas: HTMLCanvasElement): void {
    const ctx = canvas.getContext('2d');
    if (!ctx || !densityPayload || densityPayload.empty) return;

    const box = densityPayload.boundingBox;

    const baseColor = getComputedStyle(document.documentElement)
        .getPropertyValue('--ksat-surface-raised').trim();
    ctx.fillStyle = baseColor;
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // Sparse draw -- one fillRect per RETURNED bucket, never a dense loop over the bounding box
    // (Pattern 1/Pitfall 4, 233-RESEARCH.md).
    for (const bucket of densityPayload.buckets) {
        const localX = bucket.bucketX - box.minX + PADDING_BUCKETS;
        const localZ = bucket.bucketZ - box.minZ + PADDING_BUCKETS;
        ctx.fillStyle = intensityToGreen(bucket.count, densityPayload.maxCount);
        ctx.fillRect(localX * CELL_PX, localZ * CELL_PX, CELL_PX, CELL_PX);
    }

    // Death overlay draws ON TOP of the density cells (draw order: base fill -> density ->
    // death glyphs), gated by the toggle state.
    if (showDeaths) {
        drawDeaths(ctx, box);
    }
}

// Reused verbatim from mc/analytics.ts's intensityToGreen() (D-07) -- colors/thresholds locked
// byte-for-byte by UI-SPEC §7.4. Duplicated with this comment pointing at the original rather
// than extracted into a shared helper (233-UI-SPEC.md leaves that choice to discretion). The
// count===0 branch is unreachable from the sparse draw loop above (a returned bucket always has
// count>=1) -- kept for parity with the source and as defensive dead code, matching
// analytics.ts's own "fallback safety" framing of the same branch.
function intensityToGreen(count: number, maxCount: number): string {
    if (count === 0) {
        return 'var(--ksat-surface-raised)';
    }
    const ratio = count / Math.max(maxCount, 0.001);
    if (ratio < 0.25) return '#9be9a8';
    if (ratio < 0.50) return '#40c463';
    if (ratio < 0.75) return '#30a14e';
    return '#216e39';
}

// ---------------------------------------------------------------------------
// Death overlay (HEAT-02, D-05/D-06)
// ---------------------------------------------------------------------------

/** `bucketSize` MUST be the sibling density fetch's own (possibly T-233-03-coarsened)
 *  `effectiveBucketSize` (CR-01 fix, 233-REVIEW.md) -- see loadHeatmapData(), the only caller.
 *  `requestId` is that same call's `heatmapRequestId` snapshot (WR-01 fix) -- if a newer load has
 *  since started by the time this response resolves, it is discarded before touching any shared
 *  state below. */
async function fetchDeaths(canvas: HTMLCanvasElement, bucketSize: number, requestId: number): Promise<void> {
    const url = `?do=getDeathMarkers&server=${encodeURIComponent(currentServer)}`
        + `&dimension=${encodeURIComponent(currentDimension)}`
        + `&bucketSize=${encodeURIComponent(String(bucketSize))}`;

    try {
        const resp = await fetch(url, { headers: { 'X-Requested-With': 'XMLHttpRequest' } });
        if (!resp.ok) throw new Error(String(resp.status));

        const payload: DeathPayload = await resp.json();
        if (requestId !== heatmapRequestId) return; // WR-01 -- stale, discard silently
        deathClusters = payload.clusters;
        deathsEmpty = payload.empty;
        clearDeathFetchError();
    } catch (err) {
        if (requestId !== heatmapRequestId) return; // WR-01 -- stale failure, discard silently
        // Independent failure (§7.7) -- a small inline note beside the toggle, NEVER the
        // whole-page error card; the density map (this phase's primary content) stays usable.
        console.error('[mc-heatmap] Death markers fetch error:', err);
        deathClusters = [];
        deathsEmpty = true;
        showDeathFetchError();
    }

    updateDeathEmptyHelper();
    redraw(canvas); // no-op internally if density hasn't resolved/is empty yet
}

/** Draws each cluster's skull glyph (+ ×N label when count>1) on top of the density cells,
 *  using the SAME local-grid transform as the density layer (D-06 -- one shared coordinate
 *  grid). Must be called AFTER the density base-fill + bucket draw (see redraw()'s own call
 *  order: base fill -> density cells -> death glyphs). */
function drawDeaths(ctx: CanvasRenderingContext2D, box: DensityBoundingBox): void {
    if (deathClusters.length === 0) return;

    const labelColor = getComputedStyle(document.documentElement).getPropertyValue('--bs-danger').trim();
    const monoFont = getComputedStyle(document.documentElement).getPropertyValue('--ksat-font-mono').trim()
        || 'monospace';

    ctx.textBaseline = 'middle';

    for (const cluster of deathClusters) {
        const localX = cluster.bucketX - box.minX + PADDING_BUCKETS;
        const localZ = cluster.bucketZ - box.minZ + PADDING_BUCKETS;
        const cx = localX * CELL_PX + CELL_PX / 2;
        const cy = localZ * CELL_PX + CELL_PX / 2;

        ctx.textAlign = 'center';
        ctx.font = `${DEATH_GLYPH_FONT_PX}px sans-serif`;
        ctx.fillStyle = '#000'; // fillStyle is irrelevant for an emoji glyph (its own embedded
        // color renders regardless, UI-SPEC §7.5 "not a themed color at all") -- set only so a
        // stale fillStyle from the density loop above never accidentally shows through on a
        // platform that fails to render the glyph as a colored emoji.
        ctx.fillText('💀', cx, cy);

        if (cluster.count > 1) {
            ctx.textAlign = 'left';
            ctx.font = `${DEATH_LABEL_FONT_PX}px ${monoFont}`;
            ctx.fillStyle = labelColor;
            ctx.fillText(`×${cluster.count}`, cx + CELL_PX / 2 + 2, cy);
        }
    }
}

function updateDeathEmptyHelper(): void {
    const deathToggle = document.getElementById('mc-heatmap-death-toggle') as HTMLInputElement | null;
    const deathEmptyEl = document.getElementById('mc-heatmap-death-empty');
    if (!deathEmptyEl) return;
    const on = deathToggle ? deathToggle.checked : showDeaths;
    deathEmptyEl.hidden = !(on && deathsEmpty);
}

/** Find-or-create a small inline note beside the "Show deaths" toggle -- the §7.7 death-fetch
 *  failure path, deliberately NOT the whole-page `#mc-heatmap-empty` error card (that surface is
 *  reserved for the density map, the page's primary content). */
function showDeathFetchError(): void {
    const controls = document.querySelector<HTMLElement>('.mc-heatmap-death-controls');
    if (!controls) return;
    let note = controls.querySelector<HTMLElement>('.mc-heatmap-death-error');
    if (!note) {
        note = document.createElement('p');
        note.className = 'mc-heatmap-death-error text-muted small mb-0 mt-1';
        controls.appendChild(note);
    }
    note.textContent = "Couldn't load death markers. Reload the page to try again.";
    note.hidden = false;
}

function clearDeathFetchError(): void {
    const note = document.querySelector<HTMLElement>('.mc-heatmap-death-controls .mc-heatmap-death-error');
    if (note) note.hidden = true;
}

// ---------------------------------------------------------------------------
// Empty / error canvas-replacement slot (§7.7) -- reuses heatmap.latte's pre-rendered
// #mc-heatmap-empty `.chart.chart-empty` markup for BOTH the true-empty/dimension-slice-empty
// states AND the density-fetch error state (all three replace the canvas with the same visual
// slot; only the heading/body text differs).
// ---------------------------------------------------------------------------

function showEmptyState(reason: string | null): void {
    const [title, body] = reason === 'dimension-empty'
        ? [
            'No activity in this dimension',
            'No block activity has been recorded in this dimension yet. Try a different dimension tab.',
        ]
        : [
            "Block activity isn't live yet",
            "No server is reporting block placement or breaking yet. Once this server's mod sends "
                + 'its first batch, density will appear here automatically.',
        ];
    writeCanvasSlot(title, body);
}

function renderDensityError(): void {
    writeCanvasSlot(
        "Couldn't load this density map",
        'The density map could not be refreshed just now. Reload the page to try again.',
    );
}

function hideEmptyState(): void {
    const emptyEl = document.getElementById('mc-heatmap-empty');
    if (emptyEl) emptyEl.hidden = true;
}

function writeCanvasSlot(title: string, body: string): void {
    const emptyEl = document.getElementById('mc-heatmap-empty');
    if (!emptyEl) return;
    const heading = emptyEl.querySelector<HTMLElement>('.chart-empty-title');
    const bodyEl = emptyEl.querySelector<HTMLElement>('.ksat-empty-state p');
    if (heading) heading.textContent = title;
    if (bodyEl) bodyEl.textContent = body;
    emptyEl.hidden = false;
}

// ---------------------------------------------------------------------------
// Dimension tabs (§7.6, D-04)
// ---------------------------------------------------------------------------

function renderDimensionTabs(container: HTMLElement, dimensions: string[], labels: string[] | undefined, active: string): void {
    const items: HTMLElement[] = [];
    for (let i = 0; i < dimensions.length; i++) {
        const dim = dimensions[i];
        const li = document.createElement('li');
        li.className = 'nav-item';

        const a = document.createElement('a');
        a.className = 'nav-link' + (dim === active ? ' active' : '');
        a.href = '#';
        a.dataset.dimension = dim;
        // Label is resolved server-side by App\Mc\McHumanize::key('dimension', ...) and arrives
        // in the parallel `dimensionLabels` channel (Phase 241 D-03/D-27/D-28). resolveMcLabel()
        // falls back to the mechanical humanizer per element when the channel is absent or short,
        // so a modded namespace (e.g. minecells:prison) still never drops (§7.9 / D-09
        // whitelist-humanize preserved). `dimensions` deliberately stays a `string[]` here --
        // re-typing its elements to `{key,label}` would throw out of this function on an older
        // bundle and destroy the whole tab bar (D-27's measured reason for the withdrawn design).
        a.textContent = resolveMcLabel(labels?.[i], dim);

        li.appendChild(a);
        items.push(li);
    }
    container.replaceChildren(...items);
}

function markActiveTab(container: HTMLElement, dim: string): void {
    container.querySelectorAll<HTMLElement>('[data-dimension]').forEach((el) => {
        el.classList.toggle('active', el.dataset.dimension === dim);
    });
}

// ---------------------------------------------------------------------------
// BlueMap deep-link fallback (§7.8, D-02 baseline; Phase 254 D-10/D-11/D-12 dimension-aware color
// + href)
// ---------------------------------------------------------------------------

/** Resolves the whole-map button's url and map id through the SAME two helpers the tooltip links
 *  already use (`bluemapUrlFor()`/`resolveMapIdForCurrentDimension()`, below), so the button's
 *  live dimension tint and its click destination can never disagree (254-UI-SPEC.md §9.3.2). Gate
 *  is now TWO conditions, not one: absent entirely -- never disabled, never a mismatched color --
 *  when EITHER the url or the current dimension's map id fails to resolve. The second half is
 *  new: before this phase, an unresolved map id silently fell back to the overworld id, which was
 *  merely imprecise under a neutral gray button; now that this button's color tracks
 *  `currentDimension` live, a tinted-but-wrong-destination button would be actively misleading
 *  (T-254-16) -- worse than showing nothing.
 *
 *  Called on initial load and on every server switch (§7.8, unchanged) AND -- new for this phase
 *  -- again from inside fetchDensity() once `currentDimension` resolves, since that is the one
 *  point every caller of loadHeatmapData() (initial load, server switch, dimension-tab switch)
 *  funnels through; calling it only from the dimension-tab click handler would leave the button
 *  permanently absent on first page load and on a server switch, where `currentDimension` is
 *  reset to `''` before the fetch that resolves it. */
function updateBluemapLink(select: HTMLSelectElement): void {
    const slot = document.getElementById('mc-heatmap-bluemap-link');
    if (!slot) return;

    const url = bluemapUrlFor(select);
    const mapId = resolveMapIdForCurrentDimension(select);

    slot.textContent = '';
    if (!url || !mapId) return;

    const a = document.createElement('a');
    // Drops the old neutral outline classes entirely rather than layering the new ones on top --
    // the pill-button treatment zeroes the border width, and a gray outline would fight the tint.
    a.className = `btn btn-sm mc-coord-pill-btn ${dimensionPillClass(currentDimension)}`;
    a.target = '_blank';
    a.rel = 'noopener';
    // The exact detail.latte deep-link hash format (reference_bluemap_deeplink_hash_format) --
    // distance (1500, the "establishing shot") and the fixed 0:64:0 look-at point are unchanged;
    // only mapId is now dimension-aware.
    a.href = `${url}#${mapId}:0:64:0:1500:0:0:0:0:perspective`;
    a.textContent = 'View in BlueMap ↗';
    slot.appendChild(a);
}

// ---------------------------------------------------------------------------
// Tooltip link resolution (Phase 244 D-01/D-02/D-04/D-05) -- three standalone helpers reused by
// wireTooltip()'s pointermove hit-test below AND, since Phase 254 (D-10/D-11/D-12), by
// updateBluemapLink() above -- that function is now a new CALLER of `bluemapUrlFor()` and
// `resolveMapIdForCurrentDimension()` (never the reverse; neither helper calls it), so its live
// dimension tint and its click destination share one source. It keeps its own `1500`
// establishing-shot distance and its own fixed spawn-centred look-at point, unchanged by this
// section (244-RESEARCH.md Pattern 3) -- only its map-id resolution now shares this section's
// logic instead of hand-rolling a `|| 'world'` fallback of its own.
// ---------------------------------------------------------------------------

/** The exact per-coordinate BlueMap hash-fragment shape (D-05) -- used by every link this phase
 *  adds, including the density-bucket's bucket-center approximation. `100` is the ONE distance
 *  value D-05 locks for every precise-or-approximate link this file builds; D-05 explicitly
 *  rejected a second "this is fuzzy" convention for the approximate case. */
function bluemapHref(url: string, mapId: string, x: number, y: number, z: number): string {
    return `${url}#${mapId}:${x}:${y}:${z}:100:0:0:0:0:perspective`;
}

/** The selected <option>'s already-https-filtered BlueMap URL, or null. Empty-string dataset
 *  values (Latte's rendering of a NULL `bluemapUrl` column) are treated as null via `||`. No
 *  scheme re-validation client-side -- `McHeatmapModel::getHeatmapServerList()` already applied
 *  the `https://` filter server-side (T-233-08); this module cannot receive an unfiltered value. */
function bluemapUrlFor(select: HTMLSelectElement): string | null {
    return select.selectedOptions[0]?.dataset.bluemapUrl || null;
}

/** Resolves which of the three per-dimension map-id dataset attributes applies to the CURRENT
 *  dimension (Phase 244 D-01/D-02/D-04). The final branch returns null and must NEVER fall back
 *  to the overworld map id -- a modded dimension (e.g. `minecells:prison`, live on this fleet)
 *  would otherwise get a confident link into the wrong world (244-RESEARCH.md Pitfall 1). Every
 *  branch treats an empty-string dataset value (a NULL column rendered by Latte) as null via `||`. */
function resolveMapIdForCurrentDimension(select: HTMLSelectElement): string | null {
    const opt = select.selectedOptions[0];
    if (!opt) return null;
    if (currentDimension === 'minecraft:overworld') return opt.dataset.bluemapMapId || null;
    if (currentDimension === 'minecraft:the_nether') return opt.dataset.bluemapMapIdNether || null;
    if (currentDimension === 'minecraft:the_end') return opt.dataset.bluemapMapIdEnd || null;
    return null;
}

// ---------------------------------------------------------------------------
// Panzoom (copied near-verbatim from eo-map.ts's init-once config, D-01/Pattern 2) --
// the one behavioral addition beyond the verbatim copy is resetPanzoomTransform() (Pitfall 8):
// eo-map.ts never resizes its canvas post-init (one fixed-size map); this canvas resizes on
// every server/dimension switch, so the transform + max-zoom ceiling must be recomputed each time.
// ---------------------------------------------------------------------------

function wirePanzoom(canvas: HTMLCanvasElement): void {
    if (canvas.dataset.panzoomInit === '1') return; // init-once guard
    canvas.dataset.panzoomInit = '1';

    const wrap = canvas.parentElement as HTMLElement; // .mc-heatmap-canvas-wrap (gesture viewport)

    const rectW = canvas.getBoundingClientRect().width;
    const fitScale = rectW > 0 ? canvas.width / rectW : 5;

    pz = Panzoom(canvas, {
        noBind: true,             // manual bind below filters mouse (mouse stays tooltip-only)
        canvas: true,             // redirects Panzoom's cursor write to the parent
        minScale: 1,              // fitted overview is the zoom floor
        maxScale: Math.min(20, Math.max(5, fitScale)),
        pinchAndPan: true,        // two-finger pan while zoomed
        panOnlyWhenZoomed: true,  // rest state is undraggable
        cursor: '',               // preserve SCSS crosshair contract
    });

    const isNotMouse = (e: PointerEvent) => e.pointerType !== 'mouse';
    wrap.addEventListener('pointerdown', (e) => { if (isNotMouse(e)) pz?.handleDown(e); });
    document.addEventListener('pointermove', (e) => { if (isNotMouse(e)) pz?.handleMove(e); }, { passive: true });
    document.addEventListener('pointerup', (e) => { if (isNotMouse(e)) pz?.handleUp(e); }, { passive: true });

    canvas.addEventListener('panzoomend', () => {
        if (pz && pz.getScale() <= 1.001) pz.pan(0, 0, { force: true });
    });
}

/** Pitfall 8 -- the canvas backbuffer just changed size (a new bounding box); the previous
 *  pan/zoom transform no longer makes sense against it. `reset()` ignores disablePan/
 *  panOnlyWhenZoomed by default, so this always snaps back to identity. The max-zoom ceiling is
 *  also recomputed from the NEW backbuffer (eo-map.ts computes this once at init-time only,
 *  since its canvas never resizes -- this one does, on every server/dimension switch). */
function resetPanzoomTransform(canvas: HTMLCanvasElement): void {
    if (!pz) return;
    pz.reset({ animate: false });
    const rectW = canvas.getBoundingClientRect().width;
    const fitScale = rectW > 0 ? canvas.width / rectW : 5;
    pz.setOptions({ maxScale: Math.min(20, Math.max(5, fitScale)) });
}

// ---------------------------------------------------------------------------
// Tooltip (mouse-only pointermove hit-test, copied shape from eo-map.ts lines 133-173)
// ---------------------------------------------------------------------------

function wireTooltip(canvas: HTMLCanvasElement, tooltip: HTMLElement, serverSelect: HTMLSelectElement): void {
    canvas.addEventListener('pointermove', (e: PointerEvent) => {
        if (e.pointerType !== 'mouse') return; // tooltip is mouse-only (D-04 idiom)
        if (!densityPayload || densityPayload.empty) {
            tooltip.hidden = true;
            hoveredHref = null;
            canvas.style.cursor = '';
            return;
        }

        // CSS-px -> canvas-px -> cell coordinate conversion (eo-map.ts idiom).
        const rect = canvas.getBoundingClientRect();
        const cssX = e.clientX - rect.left;
        const cssY = e.clientY - rect.top;
        const canvasX = cssX * (canvas.width / rect.width);
        const canvasY = cssY * (canvas.height / rect.height);
        const cellX = Math.floor(canvasX / CELL_PX);
        const cellY = Math.floor(canvasY / CELL_PX);

        const box = densityPayload.boundingBox;
        const bucketX = cellX - PADDING_BUCKETS + box.minX;
        const bucketZ = cellY - PADDING_BUCKETS + box.minZ;

        // Hit-test order: density buckets, then death clusters -- but a death cluster (when
        // shown) takes RENDER precedence when both occupy the same bucket, since the skull is
        // drawn visually ON TOP of the density cell (draw order: base fill -> density -> death,
        // see drawDeaths()'s own docblock) -- the tooltip should reflect what's on top.
        const bucket = densityPayload.buckets.find(
            (b) => b.bucketX === bucketX && b.bucketZ === bucketZ,
        );
        const cluster = showDeaths
            ? deathClusters.find((c) => c.bucketX === bucketX && c.bucketZ === bucketZ)
            : undefined;

        if (cluster) {
            const mapId = resolveMapIdForCurrentDimension(serverSelect);
            const url = bluemapUrlFor(serverSelect);
            // Phase 244 (T-244-22) -- a real runtime guard, not a formality: during the deploy
            // window an already-loaded page can receive a payload from either side of the
            // change (an old cluster shape with no `pos` field at all).
            const posOk = Array.isArray(cluster.pos) && cluster.pos.length === 3;
            hoveredHref = (mapId && url && posOk)
                ? bluemapHref(url, mapId, cluster.pos[0], cluster.pos[1], cluster.pos[2])
                : null;
            renderDeathTooltip(tooltip, cluster, hoveredHref !== null);
            positionTooltip(tooltip, e);
            canvas.style.cursor = hoveredHref ? 'pointer' : '';
            return;
        }

        if (bucket) {
            const mapId = resolveMapIdForCurrentDimension(serverSelect);
            const url = bluemapUrlFor(serverSelect);
            // bucketX/bucketZ are grid indices (floor(blockCoord / bucketSize)), not world
            // coordinates -- convert back to a real-world bucket-center before linking, or the
            // href points at the wrong location (244-REVIEW.md CR-01).
            const bucketSize = densityPayload.effectiveBucketSize;
            const worldX = bucketX * bucketSize + bucketSize / 2;
            const worldZ = bucketZ * bucketSize + bucketSize / 2;
            // D-04 -- bucket data carries no real y, so the link defaults to sea level (64).
            hoveredHref = (mapId && url) ? bluemapHref(url, mapId, worldX, 64, worldZ) : null;

            tooltip.textContent = '';
            // Invariant (254-UI-SPEC.md §9.5): the tooltip must display the SAME coordinate the
            // click opens -- worldX/64/worldZ, the exact three values already passed into
            // bluemapHref() above, never the raw pre-conversion grid indices. approximateY marks
            // the fabricated sea-level default with a tilde; x/z are real (if bucket-coarsened)
            // centres and stay untilded. The tooltip-only fallback variant is scoped to this
            // surface only (Rule B, §9.4) -- a modded dimension can be a genuinely selectable tab.
            tooltip.appendChild(buildCoordPill({
                tag: 'span',
                x: worldX,
                y: 64,
                z: worldZ,
                dimension: currentDimension,
                href: null,
                showArrow: hoveredHref !== null,
                unknownFallback: true,
                approximateY: true,
            }));
            tooltip.appendChild(document.createTextNode(`\ncount: ${bucket.count}`));

            positionTooltip(tooltip, e);
            canvas.style.cursor = hoveredHref ? 'pointer' : '';
            return;
        }

        hoveredHref = null;
        canvas.style.cursor = '';
        tooltip.hidden = true;
    });

    canvas.addEventListener('mouseleave', () => {
        tooltip.hidden = true;
        hoveredHref = null;
        canvas.style.cursor = '';
    });

    // Phase 244 (UI-SPEC §4.2) -- the canvas itself is the click target, never a real anchor
    // inside the pointer-events:none tooltip. Reuses the hit-test result the pointermove handler
    // above already cached in `hoveredHref` -- guaranteed fresh, since pointermove always fires
    // before a click on the same target. wirePanzoom() below filters its own bind to non-mouse
    // pointer types only, so a mouse click can never be the tail end of a pan and needs no
    // drag-suppression guard here.
    canvas.addEventListener('click', () => {
        if (hoveredHref) window.open(hoveredHref, '_blank', 'noopener');
    });
}

/** Single death: a Rule B decorative pill (254-UI-SPEC.md §9.1/§9.2) built from
 *  `narrative_segments[0]` when that array is present, else the legacy narrative string verbatim
 *  (deploy-window fallback -- no arrow in this fallback path, since the arrow now belongs to the
 *  pill and there is no pill to carry it). Cluster (N>1): a "{N} deaths here" heading + up to 5
 *  stacked lines (server pre-sorts most-recent-first), each line preferring its own
 *  `narrative_segments[i]` and degrading per-line to `narratives[i]` when the segment array is
 *  absent or short (both arrays sliced to the same five-line bound and driven from one loop so
 *  the two representations cannot desynchronise) + a trailing "+{N-5} more" line. The arrow, when
 *  `showGlyph` is true, appears ONLY on the first (most-recent) line (UI-SPEC §4.5/§9.1) and must
 *  not widen to the other stacked lines: one canvas click can resolve to only one coordinate, so
 *  an arrow on every line would visually promise five click targets that cannot exist. Every node
 *  is built via createElement/textContent/appendNarrativeSegments only, matching this file's
 *  zero-raw-markup discipline (T-233-07); no anchor is ever created here (D-09) -- the tooltip
 *  stays pointer-events:none and the canvas click listener in wireTooltip() remains the sole
 *  interaction path. */
function renderDeathTooltip(tooltip: HTMLElement, cluster: DeathCluster, showGlyph: boolean): void {
    tooltip.textContent = '';

    if (cluster.count <= 1) {
        const segments = cluster.narrative_segments?.[0];
        if (Array.isArray(segments)) {
            appendNarrativeSegments(tooltip, segments, { mode: 'decorative', showArrow: showGlyph });
        } else {
            tooltip.appendChild(document.createTextNode(cluster.narratives[0] ?? ''));
        }
        return;
    }

    const heading = document.createElement('div');
    heading.textContent = `${cluster.count} deaths here`;
    tooltip.appendChild(heading);

    // Slice both arrays to the same five-line bound and drive the loop from ONE length so the
    // two representations cannot desynchronise (254-UI-SPEC.md §9.7).
    const lines = cluster.narratives.slice(0, 5);
    const segmentLines = cluster.narrative_segments?.slice(0, 5);
    for (let i = 0; i < lines.length; i++) {
        const row = document.createElement('div');
        const segments = segmentLines?.[i];
        if (Array.isArray(segments)) {
            // The first-line-only arrow rule (UI-SPEC §4.5) -- lines 1-4 get full pill chrome
            // (color, shape) but no arrow; this condition must never widen.
            appendNarrativeSegments(row, segments, { mode: 'decorative', showArrow: i === 0 && showGlyph });
        } else {
            row.appendChild(document.createTextNode(lines[i]));
        }
        tooltip.appendChild(row);
    }

    if (cluster.count > 5) {
        const more = document.createElement('div');
        more.textContent = `+${cluster.count - 5} more`;
        tooltip.appendChild(more);
    }
}

function positionTooltip(tooltip: HTMLElement, e: PointerEvent): void {
    tooltip.style.left = `${e.clientX + TOOLTIP_OFFSET_X}px`;
    tooltip.style.top = `${e.clientY + TOOLTIP_OFFSET_Y}px`;
    tooltip.hidden = false;
}
